/**
 * Remark plugin that auto-styles GFM markdown tables:
 *
 *   1. For every body cell whose textual content classifies as positive
 *      or negative (see `classifyCellSign` for the exact rules), wrap
 *      the cell's content in an `<span data-tone="...">` so CSS can
 *      colour the value.
 *
 *   2. For any column whose header reads literally as "Direction"
 *      (case-insensitive), wrap each non-empty body cell in a
 *      `<badge tone="...">label</badge>` directive so the badge plugin
 *      renders it as a `<Badge>`. The tone is content-driven (the value
 *      of the cell, not the column header).
 *
 *   3. Mark numeric columns at the row level via a `data-numeric="true"`
 *      attribute on `<th>`/`<td>` so CSS can right-align them. A column
 *      is "numeric" when ≥ 75% of its body cells classify as having a
 *      sign — which catches "% change", "Net impact", "Revenue Δ"-style
 *      columns without false-flagging a "Country" or "Notes" column.
 *
 * The plugin runs *before* `remark-tone-badges` (so the auto-emitted
 * `<badge>` directives in step 2 are picked up by that plugin). Order
 * is enforced in `upright-chat.tsx` where the remark plugin list is
 * declared.
 *
 * Streaming consideration: a partially-streamed table (only some rows
 * present) will still classify whichever cells have arrived. As more
 * rows stream in and the markdown re-renders, the classification
 * updates accordingly. There is no buffering — this is a pure mdast
 * transform that runs every render pass.
 */
import { visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Root, Table, TableCell } from 'mdast';
import { classifyCellSign, classifyDirectionCell, isDirectionColumn } from './table-sign-classifier';

/** Concatenate all `text` children of a cell into a single string. */
function cellText(cell: TableCell): string {
  let s = '';
  for (const child of cell.children) {
    if (child.type === 'text') {
      // `child` is already narrowed to a `text` node by the type guard
      // above; mdast `Text` value lives directly on the node.
      s += child.value;
    }
  }
  return s;
}

/** Replace the cell's children with a single text node — used for badge auto-emission. */
function setCellChildrenToText(cell: TableCell, value: string): void {
  cell.children = [{ type: 'text', value }] as TableCell['children'];
}

/** Wrap a cell's content in a span carrying the tone via `data` -> `data-tone`. */
function wrapCellInToneSpan(cell: TableCell, tone: 'positive' | 'negative' | 'neutral'): void {
  // mdast doesn't have a native `span` node, but mdast-util-to-hast
  // promotes a node with `data.hName` into the named hast element.
  // We use an `emphasis` carrier (which already round-trips cleanly
  // through the standard pipeline) and override its hName to `span`.
  // Wrapping in an `emphasis` whose hName is overridden keeps the cell's
  // existing children intact, so inline citation pills, badges, and
  // links inside cells continue to render.
  const wrapped: TableCell['children'][number] = {
    type: 'emphasis',
    data: {
      hName: 'span',
      hProperties: { 'data-tone': tone, className: ['table-cell-tone', `table-cell-tone--${tone}`] },
    },
    children: cell.children as any,
  } as unknown as TableCell['children'][number];
  cell.children = [wrapped];
}

/**
 * Mark a `th` or `td` cell as numeric for CSS right-align. We attach
 * `data.hProperties.data-numeric` and the matching className. Existing
 * data on the cell (e.g. from the badge wrap above) is preserved.
 */
function markCellNumeric(cell: TableCell): void {
  const data = (cell.data ?? {}) as TableCell['data'] & { hProperties?: Record<string, unknown> };
  const existingProps = (data.hProperties ?? {}) as Record<string, unknown>;
  const existingClass = existingProps.className as string | string[] | undefined;
  const classNames = Array.isArray(existingClass)
    ? [...existingClass, 'table-cell-numeric']
    : typeof existingClass === 'string'
      ? [existingClass, 'table-cell-numeric']
      : ['table-cell-numeric'];
  data.hProperties = { ...existingProps, 'data-numeric': 'true', className: classNames };
  cell.data = data;
}

const NUMERIC_COLUMN_THRESHOLD = 0.75;

export const remarkTableSignColor: Plugin<[], Root> = () => {
  return tree => {
    visit(tree, 'table', (table: Table) => {
      const rows = table.children;
      if (rows.length === 0) return;
      // `rows[0]` is typed as `TableRow` (not optional) under the
      // tsconfig in this repo — the explicit length check above is the
      // safety boundary, not the indexed access.
      const header = rows[0];
      const body = rows.slice(1);

      const directionColumnIdxs = new Set<number>();
      const signCounts: number[] = []; // per column index — count of signed body cells

      // Identify direction columns from headers.
      for (let c = 0; c < header.children.length; c++) {
        const headerCell = header.children[c];
        if (isDirectionColumn(cellText(headerCell))) {
          directionColumnIdxs.add(c);
        }
        signCounts.push(0);
      }

      // Walk body rows: per cell, classify sign and (for direction
      // columns) auto-emit a badge marker.
      for (const row of body) {
        for (let c = 0; c < row.children.length; c++) {
          const cell = row.children[c];
          const raw = cellText(cell);

          // 2. Direction column auto-badge — replaces children with the
          // badge marker; subsequent remark-tone-badges plugin renders
          // it. Only run when we can classify; otherwise the cell stays
          // untouched.
          if (directionColumnIdxs.has(c)) {
            const tone = classifyDirectionCell(raw);
            if (tone !== null) {
              const visibleLabel = raw.trim();
              if (visibleLabel.length > 0) {
                // Emit the HTML-like tag form (PLT-4590). The lax parser
                // in `tone-badge-markers.ts` accepts case-insensitive
                // attrs, smart quotes, etc.; we always emit the canonical
                // double-quoted form because it's what the system prompt
                // teaches the model and what the test fixtures pin.
                setCellChildrenToText(cell, `<badge tone="${tone}">${visibleLabel}</badge>`);
                signCounts[c] = (signCounts[c] ?? 0) + 1;
              }
            }
            continue;
          }

          // 1. Sign-coloured cell — wrap in a span data-tone="...".
          const sign = classifyCellSign(raw);
          if (sign !== null) {
            wrapCellInToneSpan(cell, sign);
            signCounts[c] = (signCounts[c] ?? 0) + 1;
          }
        }
      }

      // 3. Numeric column detection — uses the body-cell sign counts.
      const numericColumnIdxs = new Set<number>();
      const bodyRowCount = body.length || 1;
      for (let c = 0; c < signCounts.length; c++) {
        if ((signCounts[c] ?? 0) / bodyRowCount >= NUMERIC_COLUMN_THRESHOLD) {
          numericColumnIdxs.add(c);
        }
      }

      // Mark every cell in numeric columns (header + body) so CSS can right-align.
      for (const row of rows) {
        for (let c = 0; c < row.children.length; c++) {
          if (numericColumnIdxs.has(c)) {
            markCellNumeric(row.children[c]);
          }
        }
      }
    });
  };
};
